import { FormEvent, useRef, useState } from 'react';
import { PortalQuoteRespondRequestType, PortalQuoteRespondResponseType } from 'stringline-types';
import { PortalApiError, submitPortalQuoteResponse } from '../api/portal';
import Button from './Button';
import SignaturePad, { SignaturePadHandle } from './SignaturePad';

export type ResponseMode = 'accept' | 'reject' | 'request_changes';

interface ResponseFormProps {
  token: string;
  mode: ResponseMode;
  selectedLineItemIds: string[];
  onCancel: () => void;
  onSuccess: (result: PortalQuoteRespondResponseType['data']) => void;
}

const ResponseForm = ({ token, mode, selectedLineItemIds, onCancel, onSuccess }: ResponseFormProps) => {
  const [acceptorName, setAcceptorName] = useState<string>('');
  const [withSignature, setWithSignature] = useState<boolean>(false);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const sigRef = useRef<SignaturePadHandle | null>(null);

  const buildBody = (): PortalQuoteRespondRequestType | { error: string } => {
    if (mode === 'accept') {
      const name = acceptorName.trim();
      if (name === '') {
        return { error: 'Please enter your name to accept.' };
      }
      if (selectedLineItemIds.length === 0) {
        return { error: 'Please select at least one item to accept this quote.' };
      }
      const body: PortalQuoteRespondRequestType = {
        action: 'accept',
        acceptorName: name,
        selectedLineItemIds,
      };
      if (withSignature) {
        const sigBase64 = sigRef.current?.getSignatureBase64() ?? null;
        if (sigBase64) {
          return { ...body, signature: { imageBase64: sigBase64 } };
        }
      }
      return body;
    }
    if (mode === 'reject') {
      const reason = rejectionReason.trim();
      const body: PortalQuoteRespondRequestType = { action: 'reject' };
      if (reason !== '') {
        return { ...body, rejectionReason: reason };
      }
      return body;
    }
    const trimmed = message.trim();
    if (trimmed === '') {
      return { error: 'Please describe the changes you would like.' };
    }
    return { action: 'request_changes', message: trimmed };
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const built = buildBody();
    if ('error' in built) {
      setError(built.error);
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitPortalQuoteResponse(token, built);
      onSuccess(result.data);
    } catch (err) {
      if (err instanceof PortalApiError) {
        if (err.status === 409) {
          setError('This quote has already been responded to.');
        } else if (err.status === 404) {
          setError('This quote link is no longer valid.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const titles: Record<ResponseMode, string> = {
    accept: 'Accept this quote',
    reject: 'Decline this quote',
    request_changes: 'Request changes',
  };

  const submitLabel: Record<ResponseMode, string> = {
    accept: 'Confirm acceptance',
    reject: 'Confirm decline',
    request_changes: 'Send request',
  };

  const submitVariant: Record<ResponseMode, 'primary' | 'danger' | 'secondary'> = {
    accept: 'primary',
    reject: 'danger',
    request_changes: 'primary',
  };

  const canSubmitAccept = mode !== 'accept' || selectedLineItemIds.length > 0;

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-4 rounded-md border border-slate-200 bg-slate-50 p-4">
      <h3 className="text-base font-semibold text-slate-900">{titles[mode]}</h3>

      {mode === 'accept' ? (
        <>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Your full name</span>
            <input
              type="text"
              value={acceptorName}
              onChange={(e) => setAcceptorName(e.target.value)}
              required
              autoComplete="name"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={withSignature}
              onChange={(e) => setWithSignature(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            <span>Add a signature (optional)</span>
          </label>

          {withSignature ? <SignaturePad ref={sigRef} defaultTypedName={acceptorName} /> : null}
        </>
      ) : null}

      {mode === 'reject' ? (
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Reason (optional)</span>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={4}
            maxLength={5000}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Let the trades team know why you're declining (optional)"
          />
        </label>
      ) : null}

      {mode === 'request_changes' ? (
        <label className="block">
          <span className="text-sm font-medium text-slate-700">What changes would you like?</span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            required
            maxLength={5000}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Describe the adjustments you'd like to discuss"
          />
        </label>
      ) : null}

      {error ? (
        <div role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" variant={submitVariant[mode]} loading={submitting} disabled={!canSubmitAccept}>
          {submitLabel[mode]}
        </Button>
      </div>
    </form>
  );
};

export default ResponseForm;
